import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiDelete } from '../services/api';
import {
  Plus,
  RefreshCw,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Edit,
  Play,
  Pause,
} from 'lucide-react';

interface RecurringInvoice {
  id: string;
  name: string;
  client_id: string;
  client_name: string | null;
  frequency: string;
  currency: string;
  is_active: boolean;
  next_generate_date: string | null;
  total_generated: number;
}

interface PaginatedResponse {
  items: RecurringInvoice[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const frequencyLabels: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-ZA');
}

export default function Recurring() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [recurring, setRecurring] = useState<RecurringInvoice[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  const page = parseInt(searchParams.get('page') || '1');

  const fetchRecurring = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('page_size', '20');

      const data = await apiGet<PaginatedResponse>(`/api/recurring-invoices?${params}`);
      setRecurring(data.items);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to fetch recurring invoices:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecurring();
  }, [page]);

  const handleToggleActive = async (id: string) => {
    try {
      await apiPost(`/api/recurring-invoices/${id}/toggle-active`, {});
      fetchRecurring();
    } catch (err) {
      console.error('Failed to toggle status:', err);
    }
    setActionMenuId(null);
  };

  const handleGenerateNow = async (id: string) => {
    try {
      await apiPost(`/api/recurring-invoices/${id}/generate`, {});
      alert('Invoice generated successfully');
      fetchRecurring();
    } catch (err) {
      console.error('Failed to generate invoice:', err);
    }
    setActionMenuId(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this recurring invoice?')) return;
    try {
      await apiDelete(`/api/recurring-invoices/${id}`);
      fetchRecurring();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
    setActionMenuId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Recurring Invoices</h1>
          <p className="text-gray-400">{total} recurring templates</p>
        </div>
        <Link
          to="/admin/recurring/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Recurring
        </Link>
      </div>

      {/* Table */}
      <div className="bg-dark-900 rounded-xl border border-dark-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-800">
                <th className="text-left p-4 text-sm font-medium text-gray-400">Name</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Client</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Frequency</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Status</th>
                <th className="text-left p-4 text-sm font-medium text-gray-400">Next Date</th>
                <th className="text-right p-4 text-sm font-medium text-gray-400">Generated</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-800">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : recurring.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-500">
                    No recurring invoices found
                  </td>
                </tr>
              ) : (
                recurring.map((item) => (
                  <tr key={item.id} className="hover:bg-dark-800/50">
                    <td className="p-4">
                      <Link
                        to={`/admin/recurring/${item.id}`}
                        className="flex items-center gap-2 text-white hover:text-primary-500"
                      >
                        <RefreshCw className="w-4 h-4" />
                        {item.name}
                      </Link>
                    </td>
                    <td className="p-4 text-gray-300">{item.client_name || '-'}</td>
                    <td className="p-4 text-gray-300">
                      {frequencyLabels[item.frequency] || item.frequency}
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.is_active
                            ? 'bg-green-500/10 text-green-400'
                            : 'bg-gray-500/10 text-gray-500'
                        }`}
                      >
                        {item.is_active ? 'Active' : 'Paused'}
                      </span>
                    </td>
                    <td className="p-4 text-gray-400">{formatDate(item.next_generate_date)}</td>
                    <td className="p-4 text-right text-gray-300">{item.total_generated}</td>
                    <td className="p-4 text-right relative">
                      <button
                        onClick={() => setActionMenuId(actionMenuId === item.id ? null : item.id)}
                        className="p-2 hover:bg-dark-700 rounded-lg"
                      >
                        <MoreHorizontal className="w-4 h-4 text-gray-400" />
                      </button>
                      {actionMenuId === item.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-dark-800 rounded-lg shadow-lg border border-dark-700 z-10">
                          <Link
                            to={`/admin/recurring/${item.id}`}
                            className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:bg-dark-700"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </Link>
                          <button
                            onClick={() => handleGenerateNow(item.id)}
                            className="flex items-center gap-2 w-full px-4 py-2 text-gray-300 hover:bg-dark-700"
                          >
                            <Play className="w-4 h-4" />
                            Generate Now
                          </button>
                          <button
                            onClick={() => handleToggleActive(item.id)}
                            className="flex items-center gap-2 w-full px-4 py-2 text-gray-300 hover:bg-dark-700"
                          >
                            {item.is_active ? (
                              <>
                                <Pause className="w-4 h-4" />
                                Pause
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4" />
                                Activate
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="flex items-center gap-2 w-full px-4 py-2 text-red-400 hover:bg-dark-700"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-dark-800">
            <p className="text-sm text-gray-400">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', (page - 1).toString());
                  setSearchParams(params);
                }}
                disabled={page <= 1}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronLeft className="w-5 h-5 text-gray-400" />
              </button>
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('page', (page + 1).toString());
                  setSearchParams(params);
                }}
                disabled={page >= totalPages}
                className="p-2 bg-dark-800 hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
